import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BUSINESS } from "@/constants/business";
import { useLang } from "@/context/LangContext";
import { STRINGS } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vt_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#a4133c] transition-colors";

export default function Admin() {
  const { lang, toggle } = useLang();
  const t = STRINGS[lang];
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);

  const load = async (tok) => {
    try {
      const { data } = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      const sorted = [...data].sort(
        (a, b) => (a.status === "handled") - (b.status === "handled")
      );
      setBookings(sorted);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setBookings(null);
      } else {
        toast.error(t.admin.tLoadFail);
      }
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success(t.admin.welcome);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings(null);
  };

  const act = async (id, action) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (action === "delete") {
        await axios.delete(`${API}/bookings/${id}`, { headers });
        setBookings((bs) => bs.filter((b) => b.id !== id));
        toast.success(t.admin.tDeleted);
      } else {
        const next = action === "handle" ? "handled" : "new";
        await axios.patch(`${API}/bookings/${id}`, { status: next }, { headers });
        setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status: next } : b)));
        toast.success(next === "handled" ? t.admin.tHandled : t.admin.tReopen);
      }
    } catch {
      toast.error(t.admin.tFail);
    }
  };

  return (
    <div data-testid="admin-page" className="bg-[#0d0906] text-[#f5f3f0] min-h-screen antialiased">
      <nav className="border-b border-white/10 bg-[#0d0906]/90 backdrop-blur-md px-6 py-4 flex items-center justify-between max-w-5xl mx-auto">
        <a href="/" className="flex items-center gap-3">
          <span className="serif-disp text-xl tracking-wider text-white uppercase font-light">{BUSINESS.brand}</span>
          <span className="script-accent text-xl text-[#c9933b]">by Mocha</span>
          <span className="mono-lbl text-[10px] tracking-widest text-[#a4133c] uppercase border border-[#a4133c]/40 px-2 py-0.5 rounded-full">
            {t.admin.inboxTag}
          </span>
        </a>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-lang-toggle-btn"
            onClick={toggle}
            aria-label="Switch language / Cambiar idioma"
            className="mono-lbl text-[10px] tracking-widest uppercase border border-[#c9933b]/50 text-[#c9933b] hover:bg-[#c9933b]/15 px-3 py-1.5 rounded-full transition-all"
          >
            {lang === "en" ? "ES" : "EN"}
          </button>
          {token && (
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="px-4 py-2 border border-white/20 hover:border-white/50 text-neutral-200 text-xs uppercase tracking-wider rounded transition-all"
            >
              {t.admin.logout}
            </button>
          )}
        </div>
      </nav>

      {!token ? (
        <div className="max-w-md mx-auto px-6 py-24">
          <div className="bg-black/60 border border-white/15 p-8 md:p-10 rounded-2xl shadow-2xl">
            <span className="mono-lbl text-xs text-[#a4133c] tracking-[0.2em] uppercase">{t.admin.privateArea}</span>
            <h1 className="serif-disp text-3xl font-light mt-2 mb-6">{t.admin.signIn}</h1>
            <form data-testid="admin-login-form" className="space-y-5" onSubmit={login}>
              <div>
                <label htmlFor="admin-email" className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.admin.email}</label>
                <input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  placeholder={t.admin.emailPh}
                  className={inputCls}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="block text-xs uppercase tracking-widest text-neutral-400 mb-2 font-mono">{t.admin.password}</label>
                <input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  placeholder="••••••••"
                  className={inputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && (
                <p data-testid="admin-login-error" className="text-[#ff758f] text-xs font-mono">{error}</p>
              )}
              <button
                data-testid="admin-login-btn"
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-[#800f2f] to-[#a4133c] hover:opacity-95 disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-semibold rounded-lg shadow-lg transition-all"
              >
                {loading ? t.admin.signingIn : t.admin.openInbox}
              </button>
            </form>
          </div>
        </div>
      ) : (
        <main className="max-w-5xl mx-auto px-6 py-16">
          <div className="flex items-end justify-between mb-10 pb-6 border-b border-white/10">
            <div>
              <span className="mono-lbl text-xs text-[#a4133c] tracking-[0.2em] uppercase">{t.admin.listLabel}</span>
              <h1 className="serif-disp text-4xl font-light mt-2">{t.admin.listTitle}</h1>
            </div>
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(token)}
              className="px-4 py-2 border border-white/20 hover:border-white/50 text-neutral-200 text-xs uppercase tracking-wider rounded transition-all"
            >
              {t.admin.refresh}
            </button>
          </div>

          {bookings === null ? (
            <p className="text-neutral-500 text-sm font-mono">{t.admin.loading}</p>
          ) : bookings.length === 0 ? (
            <div data-testid="admin-empty-state" className="border border-dashed border-white/20 rounded-xl p-10 text-center">
              <p className="text-neutral-400 text-sm italic">{t.admin.empty}</p>
            </div>
          ) : (
            <div data-testid="admin-bookings-list" className="space-y-4">
              {bookings.map((b) => (
                <div
                  key={b.id}
                  data-testid={`booking-item-${b.id}`}
                  className={`border rounded-2xl p-6 bg-white/[0.02] transition-all grid grid-cols-1 md:grid-cols-12 gap-4 ${
                    b.status === "handled"
                      ? "border-white/5 opacity-50"
                      : "border-white/10 hover:border-[#a4133c]/50"
                  }`}
                >
                  <div className="md:col-span-4">
                    <p className="text-white font-medium">{b.name}</p>
                    <p className="text-[#c9933b] text-xs font-mono mt-1">{b.contact}</p>
                    {b.email && <p className="text-neutral-500 text-xs font-mono mt-1">{b.email}</p>}
                    <p className="text-neutral-500 text-[11px] font-mono mt-2">
                      {b.created_at ? new Date(b.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                  <div className="md:col-span-5">
                    <p className="text-neutral-200 text-sm">{b.treatment}</p>
                    <p className="text-neutral-400 text-xs mt-1 font-mono">
                      {b.preferred_time ? new Date(b.preferred_time).toLocaleString() : t.admin.noTime}
                    </p>
                    {b.notes && <p className="text-neutral-400 text-xs mt-2 italic">“{b.notes}”</p>}
                  </div>
                  <div className="md:col-span-3 flex md:flex-col md:items-end items-start gap-2">
                    <div className="flex gap-2">
                      <span
                        className={`mono-lbl text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${
                          b.session_type === "outcall"
                            ? "border-[#c9933b]/50 text-[#c9933b]"
                            : "border-[#a4133c]/50 text-[#ff758f]"
                        }`}
                      >
                        {b.session_type === "outcall" ? t.admin.outcall : t.admin.incall}
                      </span>
                      {b.gifted && (
                        <span
                          data-testid={`booking-gifted-${b.id}`}
                          className="mono-lbl text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-[#c9933b]/60 text-[#c9933b]"
                        >
                          {t.admin.gifted}
                        </span>
                      )}
                      {b.status === "handled" && (
                        <span
                          data-testid={`booking-status-${b.id}`}
                          className="mono-lbl text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-emerald-500/50 text-emerald-400"
                        >
                          {t.admin.handled}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid={`booking-handle-${b.id}`}
                        onClick={() => act(b.id, b.status === "handled" ? "reopen" : "handle")}
                        className="text-[10px] uppercase tracking-wider border border-white/20 hover:border-[#c9933b] text-neutral-300 px-3 py-1.5 rounded transition-all"
                      >
                        {b.status === "handled" ? t.admin.undo : t.admin.markHandled}
                      </button>
                      <button
                        data-testid={`booking-delete-${b.id}`}
                        onClick={() => {
                          if (window.confirm(t.admin.confirmDel)) act(b.id, "delete");
                        }}
                        className="text-[10px] uppercase tracking-wider border border-white/20 hover:border-[#9d0208] text-neutral-400 hover:text-[#ff758f] px-3 py-1.5 rounded transition-all"
                      >
                        {t.admin.del}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      )}
    </div>
  );
}
